import axios from "axios";
import {
  getRefreshToken,
  persistSession,
  tryAdoptPublishedSession,
} from "../Views/helpers/session";
import { authApiUrl } from "../config/service-urls";

// Deduplicates concurrent refreshes. Without this, parallel 401s would each POST
// /refresh-token, and every call but the first would fail against the now-blacklisted token.
let inFlightRefresh = null;

// `staleAccessToken` is the token the failed request used. If shared cookies now hold a
// strictly newer pair, adopt that instead of POSTing /refresh-token with a revoked JTI.
export const refreshSession = async (staleAccessToken) => {
  const alreadyRotated = tryAdoptPublishedSession(staleAccessToken);
  if (alreadyRotated) {
    return alreadyRotated;
  }

  // Local E2E (.localhost): handoff passes access token only — no refresh in storage.
  if (!getRefreshToken()) {
    return null;
  }

  if (!inFlightRefresh) {
    inFlightRefresh = requestNewSession().finally(() => {
      inFlightRefresh = null;
    });
  }

  try {
    return await inFlightRefresh;
  } catch (error) {
    const published = tryAdoptPublishedSession(staleAccessToken);
    if (published) {
      return published;
    }
    throw error;
  }
};

// Refreshes the session and replays a request that came back 401. Resolves with the retried
// response, or null when the session cannot be recovered.
export const retryWithRefreshedSession = async (instance, error) => {
  const request = error.config;
  if (!request || request.hasRetriedAfterRefresh || isRefreshRequest(request)) {
    return null;
  }
  request.hasRetriedAfterRefresh = true;

  const authHeaderKey = findAuthorizationHeaderKey(request.headers);
  const staleToken = stripBearerPrefix(
    authHeaderKey ? request.headers[authHeaderKey] : null,
  );

  try {
    const accessToken = await refreshSession(staleToken);
    if (!accessToken) {
      return null;
    }
    if (authHeaderKey) {
      const usesBearerPrefix = /^Bearer\s+/i.test(
        request.headers[authHeaderKey] ?? "",
      );
      request.headers[authHeaderKey] = usesBearerPrefix
        ? `Bearer ${accessToken}`
        : accessToken;
    }
    return await instance.request(request);
  } catch {
    return null;
  }
};

export const stripBearerPrefix = (headerValue) => {
  if (typeof headerValue !== "string") return null;
  return headerValue.replace(/^Bearer\s+/i, "").trim() || null;
};

const requestNewSession = async () => {
  if (!authApiUrl || authApiUrl === "/auth-ts/api/v1") {
    throw new Error("REACT_APP_SERVICES_BASE_URL is not configured");
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await axios.post(
    `${authApiUrl}/refresh-token`,
    { refreshToken },
    { timeout: 30000 },
  );

  const { accessToken, refreshToken: rotatedRefreshToken } =
    response?.data?.data ?? {};
  if (!accessToken || !rotatedRefreshToken) {
    throw new Error("Refresh response did not contain access and refresh tokens");
  }

  persistSession(accessToken, rotatedRefreshToken);
  return accessToken;
};

const isRefreshRequest = (request) =>
  typeof request.url === "string" && request.url.includes("/refresh-token");

const findAuthorizationHeaderKey = (headers) => {
  if (!headers) return null;
  return (
    Object.keys(headers).find((key) => key.toLowerCase() === "authorization") ??
    null
  );
};
